import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import UIAssetKeys from '../UIAssetKeys';
import FloodFillContainer from './FloodFillContainer';

export default class DrawingArea extends Phaser.GameObjects.Container {
  /**
   * 
   * @param {Phaser.Scene} scene 
   */
  constructor(scene) {
    super(scene);

    this.drawingBackground = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._drawing_area, this).setInteractive({ draggable: true });
    this.floodFillContainer = new FloodFillContainer(this.scene);

    this.drawingDisabled = false;
    this.currentStrokeColor = '#000000';
    this.currentStrokeSize = 2;
    this.currentGridOption = 1;
    this.currentTool = 'brush';

    /** @type {Phaser.GameObjects.Image[]} */
    this.actions = [];

    this.drawingBackground.on(Phaser.Input.Events.GAMEOBJECT_DRAG, this.paint,this);
    this.drawingBackground.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.createStroke,this);
    this.drawingBackground.on(Phaser.Input.Events.GAMEOBJECT_DRAG_END, this.saveStroke,this);

    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.actions.forEach((image) => {
        this.scene.textures.remove(image.texture.key);
      });
    });
  }

  set gridOption(value) {
    this.currentGridOption = value;
  }

  set strokeColor(value) {
    this.currentStrokeColor = value;
  }

  set strokeSize(value) {
    this.currentStrokeSize = value;
  }

  set tool(value) {
    this.currentTool = value;
  }

  createStroke (pointer) {
    if (this.drawingDisabled) return;

    // for mirroring strokes we'll
    // need to add 1 stroke for horizontal and vertical mirroring,
    // but we'll need to add 3 stokes when both horizontal and vertical mirroring
    // are turned on. they will be reflected at the horiz & vert. center lines
    // of the drawing area

    this.currentStroke = null;

    // pt2 is vertical reflection, pt3 & pt4 horizontal reflection
    // determine how far away localPt is from vertical and horizontal center

    if (this.currentTool === 'bucket') {
      const localPt = this.getLocalPoint(pointer.x, pointer.y);
      const floodFillImage = this.floodFillContainer.floodFill(this.actions, localPt.x + this.drawingBackground.width / 2, localPt.y + this.drawingBackground.height / 2, this.currentStrokeColor, this.drawingBackground.width, this.drawingBackground.height);
      this.add(floodFillImage);
      this.actions.push(floodFillImage);
      return;
    }

    // create the canvas to use
    const currentStrokeCanvas1 = this.scene.textures.createCanvas(Phaser.Utils.String.UUID(),this.drawingBackground.width,this.drawingBackground.height);

    this.currentStroke = new Phaser.GameObjects.Image(this.scene, 0,0,currentStrokeCanvas1.key);
    this.add(this.currentStroke);

    this.oldX = 0;
    this.oldY = 0;
    this.oldX2 = 0;
    this.oldY2 = 0;
    this.oldX3 = 0;
    this.oldY3 = 0;
    this.oldX4 = 0;
    this.oldY4 = 0;
  }

  paint (pointer) {
    if (this.drawingDisabled) return;

    const localPt = this.getLocalPoint(pointer.x, pointer.y);
    localPt.x += this.drawingBackground.width / 2;
    localPt.y += this.drawingBackground.height / 2;

    // pt2 is vertical reflection, pt3 & pt4 horizontal reflection
    const localPt2 = new Phaser.Math.Vector2(localPt.x, localPt.y);
    const localPt3 = new Phaser.Math.Vector2(localPt.x, localPt.y);
    const localPt4 = new Phaser.Math.Vector2(localPt.x, localPt.y);

    const drawingWidthCenter = this.drawingBackground.width / 2;
    const drawingHeightCenter = this.drawingBackground.height / 2;

    // determine how far away localPt is from vertical and horizontal center
    const vertDiff = localPt.x - drawingWidthCenter;
    const horiDiff = localPt.y - drawingHeightCenter;

    localPt2.x = drawingWidthCenter - vertDiff;

    localPt3.y = drawingHeightCenter - horiDiff;

    localPt4.x = drawingWidthCenter - vertDiff;
    localPt4.y = drawingHeightCenter - horiDiff;

    if (this.currentTool === 'brush') {
      /** @type {Phaser.Textures.CanvasTexture} */
      const canvas = this.scene.textures.get(this.currentStroke.texture.key);
      const ctx = canvas.context;
      ctx.strokeStyle = this.currentStrokeColor;
      ctx.lineWidth = this.currentStrokeSize;
      ctx.lineCap = 'round';

      if (this.oldX) {
        ctx.beginPath();

        // place the cursor from the point the line should be started 
        ctx.moveTo(this.oldX, this.oldY);

        // draw a line from current cursor position to the provided x,y coordinate
        ctx.lineTo(localPt.x, localPt.y);

        // add stroke to the line 
        ctx.stroke();
      }

      this.oldX = localPt.x;
      this.oldY = localPt.y;

      // second point
      if (this.currentGridOption === 1 || this.currentGridOption === 3) {
        if (this.oldX2) {
          ctx.beginPath();

          // place the cursor from the point the line should be started 
          ctx.moveTo(this.oldX2, this.oldY2);

          // draw a line from current cursor position to the provided x,y coordinate
          ctx.lineTo(localPt2.x, localPt2.y);

          // add stroke to the line 
          ctx.stroke();
        }

        this.oldX2 = localPt2.x;
        this.oldY2 = localPt2.y;
      }

      // third point
      if (this.currentGridOption === 1 || this.currentGridOption === 2) {
        if (this.oldX3) {
          ctx.beginPath();

          // place the cursor from the point the line should be started 
          ctx.moveTo(this.oldX3, this.oldY3);

          // draw a line from current cursor position to the provided x,y coordinate
          ctx.lineTo(localPt3.x, localPt3.y);

          // add stroke to the line 
          ctx.stroke();
        }

        this.oldX3 = localPt3.x;
        this.oldY3 = localPt3.y;
      }

      // fourth point
      if (this.currentGridOption === 1) {
        if (this.oldX4) {
          ctx.beginPath();

          // place the cursor from the point the line should be started 
          ctx.moveTo(this.oldX4, this.oldY4);

          // draw a line from current cursor position to the provided x,y coordinate
          ctx.lineTo(localPt4.x, localPt4.y);

          // add stroke to the line 
          ctx.stroke();
        }

        this.oldX4 = localPt4.x;
        this.oldY4 = localPt4.y;
      }

      canvas.refresh();
    }
    else if (this.currentTool === 'eraser') {
      if (this.oldX) {
        /** @type {Phaser.Textures.CanvasTexture} */
        const canvas = this.scene.textures.get(this.currentStroke.texture.key);
        const ctx = canvas.context;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 16;
        ctx.lineCap = 'round';

        ctx.beginPath();

        // place the cursor from the point the line should be started 
        ctx.moveTo(this.oldX, this.oldY);

        // draw a line from current cursor position to the provided x,y coordinate
        ctx.lineTo(localPt.x, localPt.y);

        // add stroke to the line 
        ctx.stroke();

        canvas.refresh();
      }

      this.oldX = localPt.x;
      this.oldY = localPt.y;
    }
  }

  saveStroke () {
    if (this.drawingDisabled) return;

    if (this.currentTool !== 'bucket') {
      // this is and array of arrays, for undo purposes
      this.actions.push(this.currentStroke);
    }
  }

  undoAction() {
    const action = this.actions.pop();

    if (!action) return;

    action.destroy();
    this.scene.textures.remove(action.texture.key);
  }

  getPrintable() {
    const rt = new Phaser.GameObjects.RenderTexture(this.scene, 0,0, this.drawingBackground.width, this.drawingBackground.height);
    rt.draw(this, this.drawingBackground.width / 2, this.drawingBackground.height / 2);
    return rt;
  }
}